/**
 * The Table tab's presentation row model (spec 05-TBL §5.1): flattens the dialog's ONE
 * shared filtered row model ({@link module:utils.filterScheduleRows}) into the pinned
 * column set the table renders and the CSV export mirrors. Purely derivational — no
 * React, no atoms — so the columns, cell states, and export share one source of truth
 * and are unit-tested without a surface.
 */

import { MILESTONE_LABELS } from './constants';
import { WELL_STATUS_SLOT_CATEGORY_KEYS } from 'utils/slot-categories';
import { wellStatusLabel } from 'utils/well-status';
import type {
  ScheduleGroup,
  DateSource,
  FilteredScheduleRow,
  MergedDate,
  MilestoneKey,
  ScheduleRow,
  SlotId,
  WellCategory,
} from './types';

/** Empty-cell indicator (em dash) — matches the eval3 slot table convention. */
export const EMPTY_CELL = '—';

/**
 * The CSV match-key header. Slot ID is NOT a visible table column (the mock omits it),
 * but the export always writes it as the round-trip match key and upload requires it, so
 * the label lives here decoupled from the visible column set.
 */
export const SLOT_ID_LABEL = 'Slot ID';

/**
 * The Status column's volume-source-category values (spec 5.1 / AC 60). Derived from the
 * row's `category`: PDP wells value from PDP, paceable inventory (LTD / permit / DUC)
 * values from a type curve, each Create New Well category reads as its own status, and
 * anything else has no source yet. The contract carries no
 * dedicated volume-source-category field, so this is the faithful derivation from what
 * the payload ships (flagged as an AC-60 PRD-sync item in the ticket).
 */
const STATUS_LABELS: Record<WellCategory | 'pdp', string> = {
  pdp: 'PDP',
  ltd: 'Type Curve',
  permit: 'Type Curve',
  duc: 'Type Curve',
  // Each Create New Well category reads as its own status, as on the capture panel.
  ...(Object.fromEntries(
    WELL_STATUS_SLOT_CATEGORY_KEYS.map((status) => [
      status,
      wellStatusLabel(status),
    ])
  ) as Record<Exclude<WellCategory, 'ltd' | 'permit' | 'duc'>, string>),
};

/**
 * The Status (volume-source category) label for a row.
 *
 * @param {WellCategory | 'pdp'} category - The row's category.
 * @return {string} `PDP` / `Type Curve` / the Create New Well status's own label, or `--`
 *   when unknown.
 */
export function statusLabel(category: WellCategory | 'pdp'): string {
  return STATUS_LABELS[category] ?? '--';
}

/**
 * The three provenance states → the export's source-annotation column values (spec 5.2).
 * PM-ratified vocabulary (PRD Q22): On file / Scheduled here / Set by you — matching the
 * on-screen legend. (This is the per-DATE source annotation, distinct from the row-level
 * "edited" adornment in {@link module:utils.rowIsEdited}, which stays "edited" per AC 54.)
 */
const SOURCE_ANNOTATION: Record<DateSource, string> = {
  onFile: 'On file',
  scheduled: 'Scheduled here',
  override: 'Set by you',
  // The fourth state. `Record<DateSource, string>` is why it is here rather than defaulting
  // to undefined in the export: a source added to the union is a COMPILE ERROR in this map,
  // which is the only reason this cell cannot silently go blank.
  derivedFromFirstProduction: 'Derived from first production',
};

/**
 * The source-annotation cell value for a milestone (blank when the milestone is
 * unscheduled — there is no provenance for an empty cell).
 *
 * @param {MergedDate} date - The effective merged date.
 * @return {string} `On file` / `Scheduled here` / `Set by you`, or empty.
 */
export function sourceAnnotation(date: MergedDate): string {
  if (date.value === null || date.source === null) {
    return '';
  }
  return SOURCE_ANNOTATION[date.source];
}

/**
 * The visible NON-date text columns, in the mock's exact order (before the date columns):
 * Property · UWI · Area · Formation · Status. Slot ID is intentionally absent — it is
 * export-only (see {@link SLOT_ID_LABEL}). The `unit` id is kept for the CSV round-trip
 * but carries the DPS area attribution (the Gantt's grouping dimension), labeled "Area".
 */
export const TABLE_TEXT_COLUMNS = [
  'wellName',
  'uwi',
  'unit',
  'formation',
  'status',
] as const;

export type ScheduleTextColumnId = (typeof TABLE_TEXT_COLUMNS)[number];

/** Human labels for the visible non-date columns (drive both the table + the export). */
export const TEXT_COLUMN_LABELS: Record<ScheduleTextColumnId, string> = {
  wellName: 'Property',
  uwi: 'UWI',
  // `unit` is the internal column id (kept for the CSV round-trip); its data + label
  // are the DPS area attribution (the Gantt's grouping dimension), shown as "Area".
  unit: 'Area',
  formation: 'Formation',
  status: 'Status',
};

/**
 * One flattened table row. `dates` carries the EFFECTIVE merged dates (base overlaid
 * with the working log) so a table edit and a chart drag render the same value. The
 * string fields back sort / per-column filter / export; the row keeps its index
 * signature so it satisfies the DataTable row contract.
 */
export interface ScheduleTableRow extends Record<string, unknown> {
  /** The internal match key (export match column, selection/override identity) — NOT a visible column. */
  slotId: SlotId;
  /** Property = the well name (or, for a well-less LTD slot, the unique slot id). */
  wellName: string;
  /** UWI = the well's API number; blank for an open LTD slot with no well. */
  uwi: string;
  unit: string;
  formation: string;
  status: string;
  /** The row's effective dates keyed by milestone (drives the date cells + export). */
  dates: Record<MilestoneKey, MergedDate>;
  /** The base payload row (locked flags, category, ids) for edit + validation. */
  source: ScheduleRow;
}

/**
 * Builds the flat table rows from the shared filtered row model. UWI is the row's API
 * number (blank for a well-less LTD slot); the Area cell is resolved to its group's NAME
 * (the DPS area attribution, e.g. "MIDLAND - Tier 1"); Status is the volume-source category.
 *
 * @param {FilteredScheduleRow[]} filtered - The dialog's shared filtered rows (with effective dates).
 * @param {ScheduleGroup[]} groups - The payload area groups (for the Area name lookup).
 * @return {ScheduleTableRow[]} The flattened table rows, in the filtered order.
 */
export function buildScheduleTableRows(
  filtered: FilteredScheduleRow[],
  groups: ScheduleGroup[]
): ScheduleTableRow[] {
  const areaNameById = new Map<number, string>(
    groups.map((group) => [group.id, group.name])
  );
  return filtered.map(({ row, dates }) => ({
    slotId: row.slotId,
    wellName: row.wellName,
    // Blank (not an em dash) for an open LTD slot with no uwi, per the mock.
    uwi: row.apiNumber ?? '',
    // The DPS area NAME (e.g. "MIDLAND - Tier 1"), resolved from the area groups —
    // falls back to the id string only if a group name is somehow missing.
    unit:
      row.groupId === null
        ? EMPTY_CELL
        : (areaNameById.get(row.groupId) ?? String(row.groupId)),
    formation: row.interval ?? EMPTY_CELL,
    status: statusLabel(row.category),
    dates,
    source: row,
  }));
}

/**
 * The date-column label (reuses the milestone labels the chart tooltips use).
 *
 * @param {MilestoneKey} field - The milestone.
 * @return {string} The column label.
 */
export function dateColumnLabel(field: MilestoneKey): string {
  return MILESTONE_LABELS[field];
}

/**
 * The date column's source-annotation header ("Permit source", ...).
 *
 * @param {MilestoneKey} field - The milestone.
 * @return {string} The source-annotation column label.
 */
export function sourceColumnLabel(field: MilestoneKey): string {
  return `${MILESTONE_LABELS[field]} source`;
}
